import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Spin, message } from "antd";
import {
  ArrowLeftOutlined, ShoppingCartOutlined,
  LeftOutlined, RightOutlined, CloseOutlined, ZoomInOutlined,
} from "@ant-design/icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { api } from "../../services/api";
import { config as appConfig } from "../../services/config";
import { useCarrito } from "../../context/CarritoContext";
import { useClient } from "../../context/ClientContext";
import { useAuth } from "../../context/AuthContext";
import { useMediaQuery } from "react-responsive";
import { resolveRad } from "../../templates/4/utils";

const BACKEND = appConfig.URL_API.replace("/api", "");
const resolveImg = (url) => {
  if (!url) return null;
  return url.startsWith("http") ? url : BACKEND + url;
};
const resolveAdjunto = (adj) => {
  const src = adj?.adjunto;
  if (!src) return null;
  if (src.startsWith("http") || src.startsWith("/")) return resolveImg(src);
  return resolveImg(`/uploads/productos/${adj.id_tabla}/${src}`);
};
const buildImages = (producto) => {
  if (producto?.adjuntos?.length) {
    return producto.adjuntos.map(a => resolveAdjunto(a)).filter(Boolean);
  }
  const img = resolveImg(producto?.imagen);
  return img ? [img] : [];
};

// ── Lightbox ─────────────────────────────────────────────────────────────────
const Lightbox = ({ images, activeIdx, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, onPrev, onNext]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.88)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <button onClick={onClose} style={{
        position: "absolute", top: 20, right: 20,
        background: "rgba(255,255,255,0.12)", border: "none",
        borderRadius: "50%", width: 40, height: 40,
        color: "#fff", fontSize: 18, cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <CloseOutlined />
      </button>
      {images.length > 1 && (
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }} style={{
          position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)",
          background: "rgba(255,255,255,0.12)", border: "none",
          borderRadius: "50%", width: 44, height: 44,
          color: "#fff", fontSize: 20, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <LeftOutlined />
        </button>
      )}
      <img
        src={images[activeIdx]} alt=""
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8, boxShadow: "0 8px 48px rgba(0,0,0,0.5)" }}
      />
      {images.length > 1 && (
        <button onClick={(e) => { e.stopPropagation(); onNext(); }} style={{
          position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)",
          background: "rgba(255,255,255,0.12)", border: "none",
          borderRadius: "50%", width: 44, height: 44,
          color: "#fff", fontSize: 20, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <RightOutlined />
        </button>
      )}
      {images.length > 1 && (
        <div style={{ position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)", color: "rgba(255,255,255,0.6)", fontSize: 13 }}>
          {activeIdx + 1} / {images.length}
        </div>
      )}
    </div>
  );
};

// ── Galería ──────────────────────────────────────────────────────────────────
const ProductGallery = ({ images, isMobile, rad }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [hovering, setHovering] = useState(false);

  const prev = useCallback(() => setActiveIdx(i => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setActiveIdx(i => (i + 1) % images.length), [images.length]);

  if (!images.length) {
    return (
      <div style={{
        width: "100%", minHeight: 320,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "#f5f5f7", borderRadius: rad, border: "1px solid #eee",
        color: "#ccc", fontSize: 13,
      }}>
        Sin imagen
      </div>
    );
  }

  const thumbs = images.length > 1 && (
    isMobile ? (
      // Mobile: thumbnails horizontal below
      <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, marginTop: 10 }}>
        {images.map((src, i) => (
          <div key={i} onClick={() => setActiveIdx(i)} style={{
            width: 64, height: 64, flexShrink: 0,
            borderRadius: 6, overflow: "hidden", cursor: "pointer",
            border: `2px solid ${i === activeIdx ? "#374151" : "#e5e7eb"}`,
            background: "#f5f5f7", padding: 4,
            transition: "border-color 0.15s",
          }}>
            <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={(e) => { e.target.style.opacity = "0.2"; }} />
          </div>
        ))}
      </div>
    ) : (
      // Desktop: thumbnails vertical on left
      <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
        {images.map((src, i) => (
          <div key={i} onClick={() => setActiveIdx(i)} style={{
            width: 68, height: 68, flexShrink: 0,
            borderRadius: 6, overflow: "hidden", cursor: "pointer",
            border: `2px solid ${i === activeIdx ? "#374151" : "#e5e7eb"}`,
            background: "#f5f5f7", padding: 4,
            transition: "border-color 0.15s",
          }}>
            <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={(e) => { e.target.style.opacity = "0.2"; }} />
          </div>
        ))}
      </div>
    )
  );

  const mainImg = (
    <div
      style={{ position: "relative", background: "#f5f5f7", borderRadius: rad, overflow: "hidden", flex: 1 }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <div
        style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 24, minHeight: isMobile ? 260 : 380, cursor: "zoom-in" }}
        onClick={() => setLightboxOpen(true)}
      >
        <img
          src={images[activeIdx]} alt=""
          style={{ maxWidth: "100%", maxHeight: isMobile ? 220 : 340, objectFit: "contain", display: "block" }}
          onError={(e) => { e.target.style.opacity = "0.2"; }}
        />
      </div>
      <div style={{
        position: "absolute", bottom: 10, right: 10,
        background: "rgba(0,0,0,0.45)", borderRadius: 6, padding: "4px 8px",
        color: "#fff", fontSize: 12, display: "flex", alignItems: "center", gap: 4,
        opacity: hovering ? 1 : 0, transition: "opacity 0.2s", pointerEvents: "none",
      }}>
        <ZoomInOutlined /> Ampliar
      </div>
      {images.length > 1 && (
        <>
          <button onClick={prev} style={{
            position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
            background: "rgba(0,0,0,0.32)", border: "none", borderRadius: "50%",
            width: 32, height: 32, color: "#fff", fontSize: 14, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            opacity: hovering ? 1 : 0, transition: "opacity 0.2s",
          }}>
            <LeftOutlined />
          </button>
          <button onClick={next} style={{
            position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
            background: "rgba(0,0,0,0.32)", border: "none", borderRadius: "50%",
            width: 32, height: 32, color: "#fff", fontSize: 14, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            opacity: hovering ? 1 : 0, transition: "opacity 0.2s",
          }}>
            <RightOutlined />
          </button>
        </>
      )}
    </div>
  );

  return (
    <>
      {isMobile ? (
        <div>
          {mainImg}
          {thumbs}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          {thumbs}
          {mainImg}
        </div>
      )}
      {lightboxOpen && (
        <Lightbox images={images} activeIdx={activeIdx} onClose={() => setLightboxOpen(false)} onPrev={prev} onNext={next} />
      )}
    </>
  );
};

// ── Card de producto asociado ─────────────────────────────────────────────────
const ArrowSvg = ({ dir }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    {dir === "left" ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
  </svg>
);

const AsociadosCarousel = ({ asociados, primary, primaryText, rad, user, isMobile, navigate, clientPath, handleAgregar }) => {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const visibles = isMobile ? 2 : 4;
  const showArrows = asociados.length > visibles;
  const pad = showArrows ? (isMobile ? 28 : 36) : 0;

  return (
    <div style={{ marginTop: isMobile ? 40 : 64 }}>
      <h2 style={{ margin: "0 0 24px", fontSize: isMobile ? 18 : 22, fontWeight: 800, color: "#111827" }}>
        Tal vez te interese…
      </h2>
      <div style={{ position: "relative", padding: `0 ${pad}px` }}>
        {showArrows && (
          <>
            <button ref={prevRef} style={{
              position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)",
              zIndex: 10, background: "none", border: "none", cursor: "pointer",
              padding: 4, display: "flex", alignItems: "center", justifyContent: "center",
              color: "#374151",
            }}>
              <ArrowSvg dir="left" />
            </button>
            <button ref={nextRef} style={{
              position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)",
              zIndex: 10, background: "none", border: "none", cursor: "pointer",
              padding: 4, display: "flex", alignItems: "center", justifyContent: "center",
              color: "#374151",
            }}>
              <ArrowSvg dir="right" />
            </button>
          </>
        )}
        <Swiper
          modules={[Autoplay, Navigation]}
          spaceBetween={isMobile ? 12 : 20}
          slidesPerView={isMobile ? 2 : 4}
          slidesPerGroup={1}
          loop={asociados.length > (isMobile ? 2 : 4)}
          navigation={showArrows ? { prevEl: prevRef.current, nextEl: nextRef.current } : false}
          onBeforeInit={(swiper) => {
            if (showArrows) {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }
          }}
          autoplay={{ delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true }}
        >
          {asociados.map((asoc) => (
            <SwiperSlide key={asoc.id}>
              <AsociadoCard
                asoc={asoc}
                primary={primary}
                primaryText={primaryText}
                rad={rad}
                user={user}
                onNavigate={() => navigate(clientPath(`/catalogo/${asoc.id}`))}
                onAdd={() => handleAgregar(asoc)}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

const AsociadoCard = ({ asoc, primary, primaryText, rad, onNavigate, onAdd, user }) => {
  const [hov, setHov] = useState(false);
  const [btnHov, setBtnHov] = useState(false);
  const images = buildImages(asoc);
  const imgSrc = images[0];

  return (
    <div
      onClick={onNavigate}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderRadius: rad, overflow: "hidden", cursor: "pointer",
        border: "1px solid #ebebeb",
        boxShadow: hov ? "0 4px 20px rgba(0,0,0,0.10)" : "0 1px 4px rgba(0,0,0,0.05)",
        transition: "box-shadow 0.2s, transform 0.2s",
        transform: hov ? "translateY(-2px)" : "none",
        background: "#fff",
      }}
    >
      {/* Imagen */}
      <div style={{
        background: "#f5f5f7", height: 180,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, overflow: "hidden",
      }}>
        {imgSrc ? (
          <img
            src={imgSrc} alt={asoc.nombre}
            style={{ maxWidth: "100%", maxHeight: 148, objectFit: "contain", transition: "transform 0.3s", transform: hov ? "scale(1.04)" : "scale(1)" }}
            onError={(e) => { e.target.style.opacity = "0.2"; }}
          />
        ) : (
          <div style={{ color: "#ccc", fontSize: 12 }}>Sin imagen</div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: "12px 14px 14px" }}>
        {asoc.codigo && (
          <p style={{ margin: "0 0 2px", fontSize: 11, color: "#9ca3af", letterSpacing: 0.4 }}>
            CÓD. {asoc.codigo}
          </p>
        )}
        <p style={{
          margin: "0 0 10px", fontSize: 13, fontWeight: 600, lineHeight: 1.35,
          color: "#1f2937",
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {asoc.nombre}
        </p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {asoc.precio ? (
            <span style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>
              $ {Number(asoc.precio).toFixed(2)}
            </span>
          ) : (
            <span />
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onAdd(); }}
            onMouseEnter={() => setBtnHov(true)}
            onMouseLeave={() => setBtnHov(false)}
            disabled={!asoc.precio && !asoc.tiene_variantes}
            style={{
              width: 34, height: 34, borderRadius: "50%",
              border: `1.5px solid ${primary}`,
              background: btnHov ? primary : "transparent",
              color: btnHov ? primaryText : primary,
              cursor: (asoc.precio || asoc.tiene_variantes) ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background 0.18s, color 0.18s",
              opacity: (asoc.precio || asoc.tiene_variantes) ? 1 : 0.4,
              flexShrink: 0,
            }}
          >
            <ShoppingCartOutlined style={{ fontSize: 14 }} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Página principal ─────────────────────────────────────────────────────────
export const ProductoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addItems } = useCarrito();
  const { theme, clientId, config , clientPath} = useClient();
  const { user } = useAuth();
  const isMobile = useMediaQuery({ query: "(max-width: 700px)" });

  const primary = theme.primaryColor || "#111";
  const primaryText = theme.primaryTextColor || "#fff";
  const fontFamily = config?.uiConfig?.t4?.brand?.font || "inherit";
  const rad = resolveRad(config?.uiConfig?.t4?.brand?.radius);
  const pl = config?.uiConfig?.t4?.productList || {};
  const pd = config?.uiConfig?.t4?.productDetail || {};
  const pageBg = pl.bgColor || "#f9f9f9";
  const showStock = !!pd.showStock;
  const showLastUnit = !!pd.showLastUnit;
  const lastUnitMessage = pd.lastUnitMessage || "¡Última unidad!";

  const [producto, setProducto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);

  const [propiedades, setPropiedades] = useState([]);
  const [variantes, setVariantes] = useState([]);
  const [seleccion, setSeleccion] = useState({});
  const [varianteActual, setVarianteActual] = useState(null);

  // btn hover states
  const [addHov, setAddHov] = useState(false);
  const [cartHov, setCartHov] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [resProducto, resVar] = await Promise.all([
          api.catalogo.getProducto(id),
          api.catalogo.getVariantesProducto(id),
        ]);
        if (resProducto.status === "success") {
          setProducto(resProducto.data);
        } else {
          message.error(resProducto.message || "Producto no encontrado");
        }
        if (resVar.status === "success") {
          const props = resVar.propiedades || [];
          const vars = resVar.variantes || [];
          setPropiedades(props);
          setVariantes(vars);
          // Auto-seleccionar la primera variante con stock
          if (props.length > 0 && vars.length > 0) {
            const primera = vars.find(v => v.stock > 0) || vars[0];
            const autoSel = {};
            primera.valores.forEach(vv => { autoSel[vv.id_propiedad] = vv.id_valor; });
            setSeleccion(autoSel);
          }
        }
      } catch {
        message.error("Error al cargar el producto");
      }
      setLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    if (propiedades.length === 0 || variantes.length === 0) { setVarianteActual(null); return; }
    const propIds = propiedades.map(p => p.id);
    const completa = propIds.every(pid => seleccion[pid]);
    if (!completa) { setVarianteActual(null); return; }
    const found = variantes.find(v =>
      propIds.every(pid => v.valores.some(vv => vv.id_propiedad === pid && vv.id_valor === seleccion[pid]))
    );
    setVarianteActual(found || null);
  }, [seleccion, variantes, propiedades]);

  const tieneVariantes = propiedades.length > 0;

  const valueHasStock = (propId, valueId) => {
    if (!variantes.length) return true;
    // Solo considerar variantes que también coincidan con el resto de las propiedades ya seleccionadas
    const otrasSelecciones = Object.entries(seleccion).filter(([pid]) => Number(pid) !== propId);
    return variantes
      .filter(v => {
        if (!v.valores.some(vv => vv.id_propiedad === propId && vv.id_valor === valueId)) return false;
        return otrasSelecciones.every(([pid, vid]) =>
          v.valores.some(vv => vv.id_propiedad === Number(pid) && vv.id_valor === vid)
        );
      })
      .some(v => v.stock > 0);
  };

  const handleAgregar = async (prod) => {
    const p = prod || producto;
    if (!p) return;
    if (prod && prod.tiene_variantes) { navigate(clientPath(`/catalogo/${prod.id}`)); return; }
    if (!user) { navigate(clientPath('/login'), { state: { volverA: location.pathname + location.search } }); return; }
    const esPrincipal = !prod;
    setAdding(true);
    const precioFinal = esPrincipal && tieneVariantes && varianteActual?.precio != null ? varianteActual.precio : p.precio;
    const combinacion = esPrincipal && tieneVariantes && varianteActual
      ? varianteActual.valores.map(v => v.nombre).join(" / ")
      : "";
    const labelVariante = combinacion ? ` (${combinacion})` : "";
    const res = await addItems({
      id_producto: p.id,
      precio_unit: precioFinal,
      producto: p.nombre + labelVariante,
      codigo: p.codigo,
      id_variante: esPrincipal && tieneVariantes ? varianteActual?.id ?? null : null,
      combinacion,
    }, 1);
    if (res?.status === "success") {
      message.success("Producto agregado al carrito");
    } else {
      message.error("No se pudo agregar el producto");
    }
    setAdding(false);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!producto) {
    return (
      <div style={{ textAlign: "center", padding: 60, fontFamily }}>
        <p style={{ fontSize: 16, color: "#9ca3af", marginBottom: 16 }}>Producto no encontrado</p>
        <button
          onClick={() => navigate(clientPath('/catalogo'))}
          style={{
            padding: "10px 24px", background: primary, color: primaryText,
            border: "none", borderRadius: rad, cursor: "pointer", fontWeight: 600, fontSize: 14,
          }}
        >
          Volver a productos
        </button>
      </div>
    );
  }

  const asociados = producto.productos_asociados || [];
  const images = buildImages(producto);
  const precio = tieneVariantes && varianteActual?.precio != null ? varianteActual.precio : producto.precio;
  const stock = tieneVariantes ? (varianteActual?.stock ?? null) : producto.stock;
  const seleccionCompleta = !tieneVariantes || propiedades.every(p => seleccion[p.id]);
  // Sin sesión no llega la cantidad, solo si hay o no.
  const sinStock = user
    ? stock !== null && stock <= 0
    : !tieneVariantes && producto.disponible === false;

  const DESC_LIMIT = 220;
  const descLarga = producto.descripcion && producto.descripcion.length > DESC_LIMIT;
  const descVisible = producto.descripcion
    ? (descExpanded || !descLarga ? producto.descripcion : producto.descripcion.slice(0, DESC_LIMIT) + "…")
    : null;

  const categorias = producto.categorias || [];
  const breadcrumb = categorias.length > 0
    ? categorias.map(c => c.nombre).join(" / ")
    : null;

  return (
    <div style={{ background: pageBg, minHeight: "70vh", fontFamily }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: isMobile ? "20px 16px 40px" : "36px 32px 60px" }}>

        {/* Back link */}
        <button
          onClick={() => navigate(clientPath('/catalogo'))}
          style={{
            background: "none", border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 6,
            color: "#6b7280", fontSize: 13, fontWeight: 500, marginBottom: 24, padding: 0,
            fontFamily: "inherit",
          }}
        >
          <ArrowLeftOutlined style={{ fontSize: 12 }} />
          Volver a productos
        </button>

        {/* Main product layout */}
        <div style={{
          display: "flex", flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 24 : 48, alignItems: "flex-start",
        }}>

          {/* ── Galería ── */}
          <div style={{ width: isMobile ? "100%" : "52%", flexShrink: 0 }}>
            <ProductGallery images={images} isMobile={isMobile} rad={rad} />
          </div>

          {/* ── Info ── */}
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* Breadcrumb / código */}
            <p style={{ margin: "0 0 8px", fontSize: 12, color: "#9ca3af", fontWeight: 500, letterSpacing: 0.3 }}>
              {breadcrumb || (producto.codigo ? `CÓD. ${producto.codigo}` : "")}
            </p>

            {/* Nombre */}
            <h1 style={{ margin: "0 0 16px", fontSize: isMobile ? 22 : 28, fontWeight: 800, lineHeight: 1.2, color: "#111827" }}>
              {producto.nombre}
            </h1>

            {/* Precio */}
            <div style={{ marginBottom: 20 }}>
                {precio ? (() => {
                  const promo = producto.promo;
                  const promoLabel = promo?.codigo === 'descuento_porcentaje' ? `-${promo.valor}%`
                    : promo?.codigo === 'descuento_fijo' ? `-$${Number(promo.valor).toFixed(0)}`
                    : promo?.codigo === 'precio_fijo' ? `$${Number(promo.valor).toFixed(0)}`
                    : promo?.codigo === '2x1' ? '2x1' : null;

                  // El 2x1 no baja el precio unitario: su beneficio depende de
                  // cuántas lleves, así que solo se muestra la etiqueta.
                  let precioFinal = null;
                  if (promo && promo.monto_minimo === 0 && precio > 0) {
                    if (promo.codigo === 'descuento_porcentaje') precioFinal = precio * (1 - promo.valor / 100);
                    else if (promo.codigo === 'descuento_fijo') precioFinal = Math.max(0, precio - promo.valor);
                    else if (promo.codigo === 'precio_fijo' && promo.valor < precio) precioFinal = promo.valor;
                  }

                  if (precioFinal != null) {
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 16, color: "#9ca3af", textDecoration: "line-through" }}>
                          $ {Number(precio).toFixed(2)}
                        </span>
                        <span style={{ fontSize: isMobile ? 24 : 30, fontWeight: 700, color: "#e53e3e" }}>
                          $ {Number(precioFinal).toFixed(2)}
                        </span>
                        {promoLabel && (
                          <span style={{
                            background: "#e53e3e", color: "#fff",
                            fontSize: 12, fontWeight: 700, padding: "3px 10px",
                            borderRadius: 6,
                          }}>
                            {promoLabel}
                          </span>
                        )}
                      </div>
                    );
                  }

                  if (promo && promo.monto_minimo > 0 && promoLabel) {
                    return (
                      <div>
                        <span style={{ fontSize: isMobile ? 24 : 30, fontWeight: 700, color: "#111827" }}>
                          $ {Number(precio).toFixed(2)}
                        </span>
                        <div style={{
                          marginTop: 8, background: "#fef2f2", border: "1px solid #fecaca",
                          borderRadius: 6, padding: "6px 10px", display: "inline-block",
                        }}>
                          <span style={{ fontSize: 12, color: "#e53e3e", fontWeight: 600 }}>
                            {promoLabel} en compras mayores a ${Number(promo.monto_minimo).toFixed(0)}
                          </span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <span style={{ fontSize: isMobile ? 24 : 30, fontWeight: 700, color: "#111827" }}>
                      $ {Number(precio).toFixed(2)}
                    </span>
                  );
                })() : (
                  <span style={{ fontSize: 15, color: "#9ca3af" }}>Sin precio asignado</span>
                )}
            </div>

            {/* Descripción */}
            {descVisible && (
              <div style={{ marginBottom: 20 }}>
                <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 700, color: "#374151" }}>Descripción</p>
                <p style={{ margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                  {descVisible}
                  {descLarga && (
                    <button
                      onClick={() => setDescExpanded(e => !e)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: primary, fontSize: 13, fontWeight: 600, padding: "0 0 0 4px", fontFamily: "inherit" }}
                    >
                      {descExpanded ? "ver menos" : "ver más"}
                    </button>
                  )}
                </p>
              </div>
            )}

            {/* Selectores de variantes */}
            {tieneVariantes && (
              <div style={{ marginBottom: 20, display: "flex", flexDirection: "column", gap: 16 }}>
                {propiedades.map(prop => {
                  const isColor = prop.tipo === "color";
                  const selectedVal = prop.valores.find(v => v.id === seleccion[prop.id]);
                  return (
                    <div key={prop.id}>
                      <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#374151" }}>
                        {prop.nombre}
                        {selectedVal && (
                          <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 8 }}>
                            {selectedVal.nombre}
                          </span>
                        )}
                      </p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: isColor ? 8 : 8 }}>
                        {prop.valores.map(v => {
                          const selected = seleccion[prop.id] === v.id;
                          const hasStock = valueHasStock(prop.id, v.id);
                          if (isColor && v.hex) {
                            return (
                              <button
                                key={v.id}
                                title={v.nombre + (!hasStock ? " — Sin stock" : "")}
                                onClick={() => setSeleccion(s => ({ ...s, [prop.id]: v.id }))}
                                style={{
                                  width: 34, height: 34, borderRadius: "50%",
                                  background: v.hex, border: "none",
                                  cursor: "pointer", flexShrink: 0,
                                  outline: selected ? `3px solid ${primary}` : "3px solid transparent",
                                  outlineOffset: 2,
                                  boxShadow: "0 0 0 1px rgba(0,0,0,0.12)",
                                  transition: "outline-color 0.15s",
                                  opacity: hasStock ? 1 : 0.35,
                                  filter: hasStock ? "none" : "grayscale(0.8)",
                                }}
                              />
                            );
                          }
                          return (
                            <button
                              key={v.id}
                              onClick={() => setSeleccion(s => ({ ...s, [prop.id]: v.id }))}
                              style={{
                                padding: "7px 16px", borderRadius: rad,
                                border: `1.5px solid ${selected ? primary : (hasStock ? "#d1d5db" : "#e5e7eb")}`,
                                background: selected ? `${primary}12` : "#fff",
                                color: selected ? primary : (hasStock ? "#374151" : "#c4c9d4"),
                                fontSize: 13, fontWeight: selected ? 700 : 400,
                                cursor: "pointer", transition: "border-color 0.15s, background 0.15s",
                                fontFamily: "inherit",
                                opacity: hasStock ? 1 : 0.55,
                                textDecoration: hasStock ? "none" : "line-through",
                              }}
                            >
                              {v.nombre}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Última unidad */}
            {showLastUnit && stock !== null && stock === 1 && (
              <p style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 600, color: "#f59e0b" }}>
                {lastUnitMessage}
              </p>
            )}

            {/* Stock disponible */}
            {showStock && stock !== null && stock > 0 && (
              <p style={{ margin: "0 0 16px", fontSize: 12, color: "#6b7280" }}>
                Stock disponible: {stock}{producto.unidad ? ` ${producto.unidad}` : ""}
              </p>
            )}

            {/* Divider */}
            <div style={{ height: 1, background: "#f3f4f6", margin: "4px 0 20px" }} />

            {/* Botones de acción */}
            {tieneVariantes && !seleccionCompleta ? (
              <button disabled style={{
                width: "100%", padding: "14px 0", borderRadius: rad,
                border: "1.5px solid #d1d5db", background: "#f9fafb",
                color: "#9ca3af", fontSize: 15, fontWeight: 600, cursor: "not-allowed", fontFamily: "inherit",
              }}>
                Seleccioná las opciones
              </button>
            ) : sinStock ? (
              <button disabled style={{
                width: "100%", padding: "14px 0", borderRadius: rad,
                border: "1.5px solid #d1d5db", background: "#f9fafb",
                color: "#9ca3af", fontSize: 15, fontWeight: 600, cursor: "not-allowed", fontFamily: "inherit",
              }}>
                Sin stock
              </button>
            ) : (
              <button
                onMouseEnter={() => setAddHov(true)}
                onMouseLeave={() => setAddHov(false)}
                disabled={user && (!precio || adding)}
                onClick={() => handleAgregar()}
                style={{
                  width: "100%", padding: "14px 0", borderRadius: rad,
                  border: "none",
                  background: addHov ? `${primary}cc` : primary,
                  color: primaryText,
                  fontSize: 14, fontWeight: 600, cursor: precio ? "pointer" : "not-allowed",
                  transition: "background 0.18s", fontFamily: "inherit",
                  opacity: precio ? 1 : 0.5,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                <ShoppingCartOutlined />
                {adding ? "Agregando..." : "Agregar al carrito"}
              </button>
            )}

            {/* Código */}
            {producto.codigo && breadcrumb && (
              <p style={{ margin: "14px 0 0", fontSize: 12, color: "#9ca3af" }}>
                Cód. {producto.codigo}
              </p>
            )}
          </div>
        </div>

        {/* ── Productos asociados ── */}
        {asociados.length > 0 && (
          <AsociadosCarousel
            asociados={asociados}
            primary={primary}
            primaryText={primaryText}
            rad={rad}
            user={user}
            isMobile={isMobile}
            navigate={navigate}
            clientPath={clientPath}
            handleAgregar={handleAgregar}
          />
        )}
      </div>
    </div>
  );
};
